import React, { useState, useRef, useCallback } from 'react';
import {
  UploadCloud,
  Folder,
  FileJson,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Image as ImageIcon,
} from 'lucide-react';
import {
  assignCategoriesToFiles,
  traverseFileSystemEntry,
} from '@/services/folderReader';
import { IngestedFileRecord } from '@/types/domain';

declare module 'react' {
  interface InputHTMLAttributes<T> {
    webkitdirectory?: string;
    directory?: string;
  }
}

export interface FolderDropzoneProps {
  onPhotosIngested: (photos: IngestedFileRecord[]) => void;
  onPolarstepsFiles: (files: { locationsFile?: File; tripFile?: File }) => void;
  isProcessing?: boolean;
  className?: string;
}

const SUPPORTED_IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|heic|heif|avif|tiff?|dng)$/i;

export const FolderDropzone: React.FC<FolderDropzoneProps> = ({
  onPhotosIngested,
  onPolarstepsFiles,
  isProcessing = false,
  className = '',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState<string>('');
  const [stagedPhotos, setStagedPhotos] = useState<IngestedFileRecord[]>([]);
  const [stagedPolarsteps, setStagedPolarsteps] = useState<{
    locationsFile?: File;
    tripFile?: File;
  }>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const folderInputRef = useRef<HTMLInputElement>(null);
  const polarstepsInputRef = useRef<HTMLInputElement>(null);

  // Group staged photos by category for summary display
  const categoryCounts = stagedPhotos.reduce<Record<string, number>>((acc, photo) => {
    acc[photo.category] = (acc[photo.category] || 0) + 1;
    return acc;
  }, {});

  // Separate raw files into photos vs Polarsteps JSON files
  const processRawFiles = useCallback(
    (rawFiles: { file: File; relativePath: string }[]) => {
      const jsonFiles: File[] = [];
      const photoCandidates: { file: File; relativePath: string }[] = [];

      for (const item of rawFiles) {
        const lowerName = item.file.name.toLowerCase();

        if (lowerName.endsWith('.json')) {
          jsonFiles.push(item.file);
        } else if (
          SUPPORTED_IMAGE_EXTENSIONS.test(lowerName) ||
          item.file.type.startsWith('image/')
        ) {
          photoCandidates.push(item);
        }
      }

      let locationsFile: File | undefined = stagedPolarsteps.locationsFile;
      let tripFile: File | undefined = stagedPolarsteps.tripFile;

      if (jsonFiles.length === 1) {
        locationsFile = jsonFiles[0];
        tripFile = jsonFiles[0];
      } else {
        for (const file of jsonFiles) {
          const lower = file.name.toLowerCase();
          if (lower.includes('location')) {
            locationsFile = file;
          } else if (lower.includes('trip')) {
            tripFile = file;
          } else if (!locationsFile) {
            locationsFile = file;
          } else if (!tripFile) {
            tripFile = file;
          }
        }
      }

      const categorized = assignCategoriesToFiles(photoCandidates);
      setStagedPhotos(categorized);
      setStagedPolarsteps({ locationsFile, tripFile });

      onPhotosIngested(categorized);
      onPolarstepsFiles({ locationsFile, tripFile });
    },
    [stagedPolarsteps, onPhotosIngested, onPolarstepsFiles]
  );

  // Handle Drag Events
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setErrorMessage(null);

      const items = e.dataTransfer.items;
      if (!items || items.length === 0) return;

      setIsScanning(true);
      setScanStatus('Scansione cartelle in corso...');

      try {
        const scannedFiles: { file: File; relativePath: string }[] = [];

        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (typeof item.webkitGetAsEntry === 'function') {
            const entry = item.webkitGetAsEntry();
            if (entry) {
              const entries = await traverseFileSystemEntry(
                entry,
                '',
                (count, dir) => setScanStatus(`Scansionati ${count} file (${dir})...`)
              );
              scannedFiles.push(...entries);
            }
          } else {
            const file = item.getAsFile();
            if (file) {
              scannedFiles.push({ file, relativePath: file.name });
            }
          }
        }

        processRawFiles(scannedFiles);
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : 'Errore durante la scansione delle cartelle.';
        setErrorMessage(msg);
      } finally {
        setIsScanning(false);
        setScanStatus('');
      }
    },
    [processRawFiles]
  );

  // Handle Folder Input Selection (<input webkitdirectory />)
  const handleFolderSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setErrorMessage(null);
      const files = e.target.files;
      if (!files || files.length === 0) return;

      setIsScanning(true);
      setScanStatus(`Caricamento di ${files.length} file...`);

      try {
        const rawFiles: { file: File; relativePath: string }[] = [];
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const relativePath = file.webkitRelativePath || file.name;
          rawFiles.push({ file, relativePath });
        }
        processRawFiles(rawFiles);
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : 'Errore durante la selezione dei file.';
        setErrorMessage(msg);
      } finally {
        setIsScanning(false);
        setScanStatus('');
      }
    },
    [processRawFiles]
  );

  // Handle Polarsteps JSON Input
  const handlePolarstepsSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setErrorMessage(null);
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const jsonFiles: File[] = [];
      for (let i = 0; i < files.length; i++) {
        if (files[i].name.toLowerCase().endsWith('.json') || files[i].type.includes('json')) {
          jsonFiles.push(files[i]);
        }
      }

      if (jsonFiles.length === 0) return;

      let locationsFile: File | undefined = undefined;
      let tripFile: File | undefined = undefined;

      if (jsonFiles.length === 1) {
        // Single JSON file: can be locations, trip, or combined export
        locationsFile = jsonFiles[0];
        tripFile = jsonFiles[0];
      } else {
        for (const file of jsonFiles) {
          const lower = file.name.toLowerCase();
          if (lower.includes('location')) {
            locationsFile = file;
          } else if (lower.includes('trip')) {
            tripFile = file;
          } else if (!locationsFile) {
            locationsFile = file;
          } else if (!tripFile) {
            tripFile = file;
          }
        }
      }

      const updated = { locationsFile, tripFile };
      setStagedPolarsteps(updated);
      onPolarstepsFiles(updated);
    },
    [onPolarstepsFiles]
  );

  return (
    <div className={`w-full max-w-2xl mx-auto flex flex-col gap-4 ${className}`}>
      {/* Hidden inputs */}
      <input
        type="file"
        ref={folderInputRef}
        onChange={handleFolderSelect}
        className="hidden"
        multiple
        webkitdirectory=""
        directory=""
      />
      <input
        type="file"
        ref={polarstepsInputRef}
        onChange={handlePolarstepsSelect}
        className="hidden"
        multiple
        accept=".json,application/json"
      />

      {/* Main Drag-and-Drop Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center text-center transition-all duration-200 cursor-pointer ${
          isDragging
            ? 'border-brand-500 bg-brand-500/10 scale-[1.01]'
            : 'border-slate-700 bg-slate-800/60 hover:border-slate-500 hover:bg-slate-800/90'
        }`}
        onClick={() => folderInputRef.current?.click()}
      >
        <div className="w-16 h-16 rounded-full bg-slate-700/60 flex items-center justify-center mb-4 text-brand-500">
          {isScanning ? (
            <RefreshCw className="w-8 h-8 animate-spin" />
          ) : (
            <UploadCloud className="w-8 h-8" />
          )}
        </div>

        <h3 className="text-lg font-semibold text-slate-100 mb-1">
          {isScanning ? 'Scansione in corso...' : 'Trascina qui le cartelle delle foto'}
        </h3>
        <p className="text-sm text-slate-400 max-w-md mb-6">
          Supporta sottocartelle per categorie (es. <em>"Foto con me"</em>, <em>"Panorami"</em>) e
          i file Polarsteps (<em>locations.json</em>, <em>trip.json</em>).
        </p>

        {/* Action Buttons */}
        <div
          className="flex flex-wrap items-center justify-center gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            disabled={isScanning || isProcessing}
            onClick={() => folderInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-500 text-white rounded-lg text-sm font-medium transition shadow-md shadow-brand-600/20 disabled:opacity-50"
          >
            <Folder className="w-4 h-4" />
            Seleziona Cartella Foto
          </button>

          <button
            type="button"
            disabled={isScanning || isProcessing}
            onClick={() => polarstepsInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm font-medium transition disabled:opacity-50"
          >
            <FileJson className="w-4 h-4" />
            Carica Dati Polarsteps
          </button>
        </div>

        {scanStatus && (
          <p className="mt-4 text-xs text-brand-500 font-mono animate-pulse">{scanStatus}</p>
        )}
      </div>

      {/* Error Alert */}
      {errorMessage && (
        <div className="flex items-center gap-2 p-3 bg-red-950/50 border border-red-800 rounded-xl text-red-200 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-400" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Staging Summary */}
      {(stagedPhotos.length > 0 ||
        stagedPolarsteps.locationsFile ||
        stagedPolarsteps.tripFile) && (
        <div className="bg-slate-800/80 border border-slate-700/80 rounded-xl p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-400 uppercase tracking-wider">
            <span>Riepilogo File Riconosciuti</span>
            <span className="text-brand-500">{stagedPhotos.length} foto pronte</span>
          </div>

          {/* Polarsteps Status Badges */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            {stagedPolarsteps.locationsFile && stagedPolarsteps.tripFile && stagedPolarsteps.locationsFile === stagedPolarsteps.tripFile ? (
              <div className="sm:col-span-2 flex items-center gap-2 p-2 rounded-lg border bg-emerald-950/30 border-emerald-800/60 text-emerald-300">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="truncate">
                  Dati Polarsteps: <span className="font-mono font-medium">{stagedPolarsteps.locationsFile.name}</span> (Traccia GPS & Viaggio)
                </span>
              </div>
            ) : (
              <>
                <div
                  className={`flex items-center gap-2 p-2 rounded-lg border ${
                    stagedPolarsteps.locationsFile
                      ? 'bg-emerald-950/30 border-emerald-800/60 text-emerald-300'
                      : 'bg-slate-900/50 border-slate-800 text-slate-400'
                  }`}
                >
                  {stagedPolarsteps.locationsFile ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  )}
                  <span className="truncate">
                    {stagedPolarsteps.locationsFile
                      ? `GPS: ${stagedPolarsteps.locationsFile.name}`
                      : 'locations.json (Opzionale)'}
                  </span>
                </div>

                <div
                  className={`flex items-center gap-2 p-2 rounded-lg border ${
                    stagedPolarsteps.tripFile
                      ? 'bg-emerald-950/30 border-emerald-800/60 text-emerald-300'
                      : 'bg-slate-900/50 border-slate-800 text-slate-400'
                  }`}
                >
                  {stagedPolarsteps.tripFile ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  )}
                  <span className="truncate">
                    {stagedPolarsteps.tripFile
                      ? `Viaggio: ${stagedPolarsteps.tripFile.name}`
                      : 'trip.json (Opzionale)'}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Category Chips */}
          {Object.keys(categoryCounts).length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {Object.entries(categoryCounts).map(([cat, count]) => (
                <div
                  key={cat}
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-700/80 border border-slate-600 rounded-full text-xs text-slate-200"
                >
                  <ImageIcon className="w-3.5 h-3.5 text-brand-500" />
                  <span className="font-medium">{cat}</span>
                  <span className="px-1.5 py-0.2 bg-slate-900 rounded-full text-[10px] text-slate-400">
                    {count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FolderDropzone;
